import React from "react";
import { Button, Chip } from "@mui/material";

const NEUTRAL_CHIP_SX = { bgcolor: "#f5f5f5", color: "#94a3b8" };
const SUCCESS_CHIP_SX = { bgcolor: "#f5f5f5", color: "#2e7d32" };
const WARNING_CHIP_SX = { bgcolor: "#f5f5f5", color: "#ed6c02" };
const ERROR_CHIP_SX = { bgcolor: "#f5f5f5", color: "#d32f2f" };
const INFO_CHIP_SX = { bgcolor: "#f5f5f5", color: "#0288d1" };

export const PR_STATUS_CHIP_PROPS = {
  created: { label: "Draft", sx: NEUTRAL_CHIP_SX },
  approval_in_progress: { label: "Approval in Progress", sx: WARNING_CHIP_SX },
  approved: { label: "Approved", sx: SUCCESS_CHIP_SX },
  rejected: { label: "Rejected", sx: ERROR_CHIP_SX },
  reconsider: { label: "Reconsider", sx: WARNING_CHIP_SX },
  cancelled: { label: "Cancelled", sx: NEUTRAL_CHIP_SX },
  converted: { label: "Converted to PO", sx: INFO_CHIP_SX },
};

export const PO_STATUS_CHIP_PROPS = {
  draft: { label: "Draft", sx: NEUTRAL_CHIP_SX },
  approval_in_progress: { label: "Approval In Progress", sx: WARNING_CHIP_SX },
  approved: { label: "Approved", sx: SUCCESS_CHIP_SX },
  rejected: { label: "Rejected", sx: ERROR_CHIP_SX },
  reconsider: { label: "Reconsider", sx: WARNING_CHIP_SX },
  cancelled: { label: "Cancelled", sx: NEUTRAL_CHIP_SX },
  short_close: { label: "Short Close", sx: INFO_CHIP_SX },
};

const chipPropsFor = (statusMap, statusKey) =>
  statusMap[statusKey] || { label: statusKey || "-", sx: NEUTRAL_CHIP_SX };

export const renderStatusChip = (statusMap, statusKey) => {
  const props = chipPropsFor(statusMap, statusKey);
  return <Chip label={props.label} sx={props.sx} size="small" />;
};

// Row-level release status, derived purely from the PR's own item quantities —
// needs no PO/GRN/invoice fetch, so it's available as soon as the PR list loads.
export const getPoReleaseStatus = (pr) => {
  const items = pr?.items || [];
  const totalQty = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const convertedQty = items.reduce((sum, item) => sum + (item.convertedQuantity || 0), 0);

  if (totalQty <= 0 || convertedQty <= 0) {
    return { label: "Not Raised", sx: NEUTRAL_CHIP_SX };
  }
  if (convertedQty >= totalQty) {
    return { label: "Fully Released", sx: SUCCESS_CHIP_SX };
  }
  return { label: "Partially Released", sx: WARNING_CHIP_SX };
};

export const renderPoReleaseStatusChip = (pr) => {
  const props = getPoReleaseStatus(pr);
  return <Chip label={props.label} sx={props.sx} size="small" />;
};

// Row-level GRN Status — aggregated across every PO linked to this PR (via
// poDetailState, populated as soon as each PO's link is known, not gated on
// row expansion — the summary row needs the real rollup, not a placeholder).
export const getGrnStatusForRow = (rowState, poDetailState) => {
  if (!rowState || rowState.status === "idle" || rowState.status === "loading") {
    return { label: "Loading…", sx: NEUTRAL_CHIP_SX };
  }
  if (rowState.status === "error") {
    return { label: "Unavailable", sx: ERROR_CHIP_SX };
  }

  const purchaseOrderIds = rowState.purchaseOrderIds || [];
  if (purchaseOrderIds.length === 0) {
    return { label: "Not Started", sx: NEUTRAL_CHIP_SX };
  }

  const grnStates = purchaseOrderIds.map((po) => poDetailState?.[po.id || po._id]?.grn);
  if (grnStates.some((state) => !state || state.status === "idle" || state.status === "loading")) {
    return { label: "Loading…", sx: NEUTRAL_CHIP_SX };
  }

  const loadedStates = grnStates.filter((state) => state.status === "loaded");
  if (loadedStates.length === 0) {
    return { label: "Unavailable", sx: ERROR_CHIP_SX };
  }

  const records = loadedStates.flatMap((state) => state.records || []);
  if (records.length === 0) {
    return { label: "Pending", sx: WARNING_CHIP_SX };
  }

  const orderedQty = purchaseOrderIds.reduce(
    (sum, po) => sum + (po.items || []).reduce((itemSum, item) => itemSum + (item.quantity || 0), 0),
    0
  );
  const receivedQty = records.reduce(
    (sum, grn) =>
      sum + (grn.receivedItems || []).reduce((itemSum, item) => itemSum + (item.quantity || 0), 0),
    0
  );

  if (orderedQty > 0 && receivedQty >= orderedQty) {
    return { label: "Completed", sx: SUCCESS_CHIP_SX };
  }
  return { label: "Partially Received", sx: WARNING_CHIP_SX };
};

export const renderRowGrnStatusChip = (rowState, poDetailState) => {
  const props = getGrnStatusForRow(rowState, poDetailState);
  return <Chip label={props.label} sx={props.sx} size="small" />;
};

// Row-level Invoice Status — aggregated the same way as GRN status above.
export const getInvoiceStatusForRow = (rowState, poDetailState) => {
  if (!rowState || rowState.status === "idle" || rowState.status === "loading") {
    return { label: "Loading…", sx: NEUTRAL_CHIP_SX };
  }
  if (rowState.status === "error") {
    return { label: "Unavailable", sx: ERROR_CHIP_SX };
  }

  const purchaseOrderIds = rowState.purchaseOrderIds || [];
  if (purchaseOrderIds.length === 0) {
    return { label: "Not Uploaded", sx: NEUTRAL_CHIP_SX };
  }

  const invoiceStates = purchaseOrderIds.map((po) => poDetailState?.[po.id || po._id]?.invoice);
  if (
    invoiceStates.some((state) => !state || state.status === "idle" || state.status === "loading")
  ) {
    return { label: "Loading…", sx: NEUTRAL_CHIP_SX };
  }

  const loadedStates = invoiceStates.filter((state) => state.status === "loaded");
  if (loadedStates.length === 0) {
    return { label: "Unavailable", sx: ERROR_CHIP_SX };
  }

  const records = loadedStates.flatMap((state) => state.records || []);
  if (records.length === 0) {
    return { label: "Pending", sx: WARNING_CHIP_SX };
  }

  return { label: `Uploaded (${records.length})`, sx: SUCCESS_CHIP_SX };
};

export const renderRowInvoiceStatusChip = (rowState, poDetailState) => {
  const props = getInvoiceStatusForRow(rowState, poDetailState);
  return <Chip label={props.label} sx={props.sx} size="small" />;
};

export const getGrnProgressForPo = (po, grnState) => {
  if (!grnState || grnState.status === "idle" || grnState.status === "loading") {
    return { label: "Loading…", sx: NEUTRAL_CHIP_SX };
  }
  if (grnState.status === "error") {
    return { label: "Unavailable", sx: ERROR_CHIP_SX };
  }
  const records = grnState.records || [];
  if (records.length === 0) {
    return { label: "Not Started", sx: NEUTRAL_CHIP_SX };
  }

  const orderedQty = (po?.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
  const receivedQty = records.reduce(
    (sum, grn) =>
      sum + (grn.receivedItems || []).reduce((itemSum, item) => itemSum + (item.quantity || 0), 0),
    0
  );

  if (orderedQty > 0 && receivedQty >= orderedQty) {
    return { label: "Complete", sx: SUCCESS_CHIP_SX };
  }
  return { label: `Partial (${receivedQty}/${orderedQty || "-"})`, sx: WARNING_CHIP_SX };
};

export const renderGrnProgressChip = (po, grnState) => {
  const props = getGrnProgressForPo(po, grnState);
  return <Chip label={props.label} sx={props.sx} size="small" />;
};

export const getInvoiceStatusForPo = (po, invoiceState) => {
  if (!invoiceState || invoiceState.status === "idle" || invoiceState.status === "loading") {
    return { label: "Loading…", sx: NEUTRAL_CHIP_SX };
  }
  if (invoiceState.status === "error") {
    return { label: "Unavailable", sx: ERROR_CHIP_SX };
  }
  const records = invoiceState.records || [];
  if (records.length === 0) {
    return { label: "Not Uploaded", sx: NEUTRAL_CHIP_SX };
  }

  const hasUploaded = records.some((invoice) => (invoice.invoiceUrls || []).length > 0);
  if (!hasUploaded) {
    return { label: "Pending Upload", sx: WARNING_CHIP_SX };
  }

  const paymentCompleted = records.some((invoice) => invoice.paymentStatus === "payment_completed");
  return {
    label: paymentCompleted ? "Payment Completed" : "Uploaded",
    sx: SUCCESS_CHIP_SX,
  };
};

export const renderInvoiceStatusChip = (po, invoiceState) => {
  const props = getInvoiceStatusForPo(po, invoiceState);
  return <Chip label={props.label} sx={props.sx} size="small" />;
};

// Modeled on getRelatedPOColumns in purchase-requisition/pr-details/index.js,
// extended with the GRN/invoice columns this section needs.
export const getRelatedPoDetailColumns = (navigate, poDetailState) => [
  {
    label: "PO Number",
    id: "poNumber",
    render: (po) => po.poNumber || "-",
  },
  {
    label: "Vendor",
    id: "vendorName",
    render: (po) =>
      po.vendorCode ? `${po.vendorCode} - ${po.vendorName || ""}` : po.vendorName || "-",
  },
  {
    label: "PO Status",
    id: "status",
    render: (po) => renderStatusChip(PO_STATUS_CHIP_PROPS, po.status),
  },
  {
    label: "GRN Progress",
    id: "grnProgress",
    render: (po) => renderGrnProgressChip(po, poDetailState?.[po.id || po._id]?.grn),
  },
  {
    label: "Invoice Status",
    id: "invoiceStatus",
    render: (po) => renderInvoiceStatusChip(po, poDetailState?.[po.id || po._id]?.invoice),
  },
  {
    label: "Actions",
    id: "actions",
    render: (po) => (
      <Button
        size="small"
        variant="outlined"
        onClick={() => navigate(`purchase-order/${po.id || po._id}`)}
      >
        View PO
      </Button>
    ),
  },
];
